<template>
  <v-row class="mt-10" justify="space-between">
    <v-col md="9" sm="12" lg="9" class="carousel-wrapper mt-5">
      <v-carousel
        v-if="blogs.length"
        hide-delimiters
        class="carousel"
        v-model="model"
      >
        <v-carousel-item
          v-for="(blog, i) in blogs"
          :to="`/blogs/${blog.slug}`"
          :key="i"
          class="carousel-item"
          :style="{
            background: `rgba(0, 0, 0, .65) url(${
              blog.image || defaultBlogCover
            })`
          }"
        >
          <v-sheet tile style="background: transparent; height: 75%">
            <v-row
              class="fill-height ml-15 mt-sm-15"
              :align="$vuetify.breakpoint.xs ? 'center' : 'start'"
              justify="start"
            >
              <div class="text-h2">
                <v-btn class="primary--text font-weight-bold" color="white"
                  >Featured</v-btn
                >
                <h6
                  class="white--text font-weight-bold text-wrap d-block"
                  style="max-width: 40rem"
                >
                  {{ blog.title }}
                </h6>
              </div>
            </v-row>
          </v-sheet>
        </v-carousel-item>
      </v-carousel>
      <v-skeleton-loader
        v-if="loading"
        class="mx-auto"
        type="card"
      />
      <NoData
        v-if="!loading && !blogs.length"
        :imageWidth="200"
        title=""
        subtitle="No featured blogs"
        :image="emptyPage"
      />
      <!-- blogs by grade -->
      <v-tabs
        @change="handleTabChange"
        color="primary"
        left
        class="mt-10"
        v-if="!loadingGrades"
        v-model="activeGradeTab"
      >
        <v-tab
          v-for="(grade, i) in grades"
          :key="i"
          class="primary--text font-weight-bold"
          :disabled="loadingGradeBlogs"
          >{{ grade.grade }}</v-tab
        >
        <v-tabs-items v-model="activeGradeTab">
          <v-tab-item v-for="(_, i) in grades" :key="i">
            <v-container fluid>
              <v-row v-if="!loadingGradeBlogs">
                <v-col
                  v-for="(blog, i) in gradeBlogs"
                  :key="i"
                  cols="12"
                  md="4"
                >
                  <BlogCard :blog="blog" card-width="100%" :isDraft="false"/>
                </v-col>
              </v-row>

              <v-row v-else>
                <v-col v-for="i in 6" :key="i" cols="12" md="4">
                  <v-skeleton-loader
                    class="mx-auto"
                    max-width="300"
                    type="card"
                  ></v-skeleton-loader>
                </v-col>
              </v-row>

              <NoData
                v-if="!loadingGradeBlogs && !gradeBlogs.length"
                subtitle="No Blogs for this grade"
                title=""
                :image="emptyPage"
              />
            </v-container>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <v-skeleton-loader
        v-else
        class="mx-auto mt-5"
        type="card"
      />
      <NoData
          v-if="!loadingGrades && !grades.length"
          :imageWidth="200"
          subtitle="No grades found"
          title=""
          :image="emptyPage"
      />
      <!-- blogs by grade -->
    </v-col>
    <v-col md="3" sm="12" lg="3" class="mt-5 ml-auto">
      <v-text-field
        class="hidden-sm-and-down"
        flat
        solo
        append-icon="mdi-magnify"
        placeholder="Search"
      />
      <!-- top contributors -->
      <v-card class="mt-0 px-2 py-2" elevation="0" v-if="!loadingContributors">
        <v-list subheader>
          <v-subheader>Top Contributors</v-subheader>
          <v-list-item
            v-for="contributor in topcontributors"
            :key="contributor.uuid"
            :to="`/mentors/${contributor.uuid}`"
          >
            <v-list-item-avatar color="primary">
              <v-img
                v-if="contributor.profile.avatar"
                :src="contributor.profile.avatar"
              />
              <v-avatar v-else size="100%" color="primary">
                <span class="white--text text-h6">
                  {{initials(
                  contributor.profile.first_name,
                  contributor.profile.last_name
                )}}
                </span>
              </v-avatar>

            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title
                v-text="`@${contributor.username}`"
              ></v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-btn
          v-if="topcontributors.length"
          color="primary"
          width="50%"
          x-small
          class="d-flex mx-auto"
          to="/tutors"
          >
          See more
        </v-btn>
        <v-btn text v-else color="error" small class="d-flex mx-auto">
          No contributors found
        </v-btn>
      </v-card>
      <v-skeleton-loader
        v-else
        class="mx-auto"
        max-width="300"
        type="card"
      />
      <!-- top blogs -->
      <v-card class="mt-5 px-2 py-2" elevation="0" v-if="!topBlogsLoading">
        <v-list subheader>
          <v-subheader>Top Blogs</v-subheader>
          <v-list-item :to="`/blogs/${blog.slug}`" v-for="blog in topBlogs" :key="blog.title">
            <v-list-item-avatar tile>
              <v-img
                :alt="`${blog.title} avatar`"
                :src="blog.image || defaultBlogCover"
                contain
                class="avatar"
              />
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-subtitle
                class="font-weight-bold"
                v-text="blog.title"
              />
              <v-list-item-subtitle class="text-capitalize" v-text="`@${getDisplayNameOrEmail(blog.owner)}`"/>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-btn
          v-if="topBlogs.length"
          width="50%"
          to="/blogs/popular"
          color="primary"
          x-small
          class="d-flex mx-auto"
        >
          See more
        </v-btn>
        <v-btn text v-else color="error" small class="d-flex mx-auto">
          No Blogs found
        </v-btn>
      </v-card>
      <v-skeleton-loader
        v-else
        class="mx-auto mt-5"
        max-width="300"
        type="card"
      />
    </v-col>
  </v-row>
</template>

<script>
import { constructDate } from "@/utilz/date";
import { kFormatter } from "../../utilz/kFormatter";
import NoData from "@/components/NoData";
import defaultBlogCover from "@/assets/default-blog-cover.png";
import emptyPage from "@/assets/no_data.svg";
import BlogCard from "@/components/BlogCard";
import { getDisplayNameOrEmail } from "@/utilz/dispalyNameOrEmail";

export default {
  components: { NoData, BlogCard },
  metaInfo() {
    return {
      title: "Guiomentor - Blogs",
      url: window.location.href,
    };
  },
  data: () => ({
    model: 0,
    colors: ["primary", "secondary", "yellow darken-2", "red", "orange"],
    loading: false,
    blogs: [],
    limit: 10,
    defaultBlogCover,
    emptyPage,
    grades: [],
    loadingGrades: false,
    loadingGradeBlogs: false,
    gradeBlogs: [],
    activeGradeTab: null,
    loadingContributors: false,
    topBlogs: [],
    topBlogsLoading: false,
    topcontributors: [],
  }),
  mounted() {
    this.loadBlogs();
    this.loadGrades();
    this.loadTopBlogs();
    this.loadMentors();
  },
  computed: {
    hasData() {
      return Boolean(this.blogs.length);
    },
  },
  methods: {
    getDisplayNameOrEmail(user) {
      return getDisplayNameOrEmail(user);
    },
    initials(firstname, lastname) {
      let fname = firstname ? firstname.charAt(0).toUpperCase() : "";
      let lname = lastname ? lastname.charAt(0).toUpperCase() : "";
      return fname + lname;
    },
    async loadMentors() {
      try {
        this.loadingContributors = true;
        let { data } = await this.axios.get(
          `/users/top-contributors/?country=UG&limit=6`
        );
        this.topcontributors = data.results;
        this.loadingContributors = false;
      } catch (error) {
        this.loadingContributors = false;
      }
    },
    async loadBlogs() {
      try {
        this.loading = true;
        const { data } = await this.axios.get(
          `blogs/posts/?limit=${this.limit}&order_by=blog_views`
        );
        this.blogs = data.results;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$snackbar.showMessage({
          content: this.$t("errors.somethingWentWrongError"),
          color: "error",
        });
      }
    },
    async loadTopBlogs() {
      try {
        this.topBlogsLoading = true;
        const { data } = await this.axios.get(
          `blogs/posts/?limit=${this.limit}&order_by=blog_views`
        );
        this.topBlogs = data.results;
        this.topBlogsLoading = false;
      } catch (error) {
        this.topBlogsLoading = false;
        this.$snackbar.showMessage({
          content: this.$t("errors.somethingWentWrongError"),
          color: "error",
        });
      }
    },
    async loadGrades() {
      try {
        this.loadingGrades = true;
        const { data } = await this.axios.get(
          "class_grade/?country_code=UG&limit=3"
        );
        this.grades = data.results;
        this.loadingGrades = false;
      } catch (error) {
        this.$snackbar.showMessage({
          content: this.$t("errors.somethingWentWrongError"),
          color: "error",
        });
      } finally {
        this.loadingGrades = false;
      }
    },
    blogOwnerName: (owner) => {
      const { first_name, last_name } = owner.profile;
      if (last_name || first_name)
        return `${first_name || ""} ${last_name || ""}`;
      return owner.username;
    },
    blogCreatedAt(createdAt) {
      const date = new Date(createdAt);
      return constructDate(date.getDate(), date.getMonth(), date.getFullYear());
    },
    kFormatter(value) {
      return kFormatter(value);
    },
    async handleTabChange(e) {
      const grade = this.grades[e];
      if (grade.grade) {
        this.loadingGradeBlogs = true;
        this.gradeBlogs = [];
        try {
          const { data } = await this.axios.get(
              `/blogs/grade/${grade.uuid}/posts?limit=10`
          );
          this.gradeBlogs = data.results;
          this.loadingGradeBlogs = false;
        } catch (error) {
          this.loadingGradeBlogs = false;
          this.$snackbar.showMessage({
            content: this.$t("errors.somethingWentWrongError"),
            color: "error",
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables";


.carousel {
  height: 22rem !important;
  border-radius: 4rem;
}


@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .carousel {
    border-radius: 1rem;
  }
}

@mixin background {
  background-color: #ffffff;
  color: #0035b0;
}
.search-input {
  @include background;
  height: 2rem;
}

.carousel-wrapper {
  @include background;
  border-radius: 1rem;
  padding: 1rem;
}

.top-contributers {
  @include background;
}
.carousel-item {
  background-size: cover;
  background-position: center;
  background-blend-mode: darken;
  background-repeat: no-repeat;
}

.v-image__image--contain {
  background-size: cover  !important;
}
.top-contributers-wrapper {
  background-color: #ffffff;
  color: #0035b0;
  border-radius: 1rem;
  padding: 1rem;
}
</style>
